import './style/salesProductStats.scss';
import {AwesomeIcon, ChangeFunction, Loader} from '@dvrd/dvr-controls';
import {roundToNumber} from '@dvrd/dvr-controls/src/js/util/miscUtil';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PeriodLabel, SalesProductView, StatCardDataType} from '../../../../util/enums';
import {BolmatePlotSeries, DataSplitMethod} from '../../../../util/interfaces';
import {
    AggregatedSalesProduct,
    GeneralSalesProduct,
    SalesDashboardGraphData,
    SpecificSalesProduct
} from '../../../../util/types';
import {formatCurrency} from '../../../../util/utils';
import {BolmateButton, BolmatePeriodSelector, BolmatePlot, BolmateStatCard, BolmateTooltip} from '../../../controls';
import BolmateSwitch from '../../../controls/switch/bolmateSwitch';
import {ApexOptions} from 'apexcharts';
import {Icon} from '@iconify/react';

interface Props {
    onChangePeriod: (range: [IDate, IDate], presetLabel: PeriodLabel | null) => void;
    onChangeView: (view: SalesProductView) => MouseEventHandler;
    onNavigateBol: MouseEventHandler;
    onChangeCumulativeGraphs: (key: string) => ChangeFunction<boolean>;
    entries: Array<SpecificSalesProduct>;
    period: PeriodLabel;
    customPeriodStart: IDate | null;
    customPeriodEnd: IDate | null;
    groupMethod: DataSplitMethod;
    loading: boolean;
    product: GeneralSalesProduct | null;
    aggregatedPeriod: AggregatedSalesProduct;
    aggregatedComparison: AggregatedSalesProduct;
    cumulativeEntries: Array<SpecificSalesProduct>;
    cumulativeGraphs: Set<string>;
}

type GraphTypeMap = Record<string, 'bar' | 'area'>;

export default function SalesProductStats(props: Props) {
    const {t} = useTranslation();
    const {
        onChangePeriod,
        onNavigateBol,
        onChangeView,
        onChangeCumulativeGraphs,
        entries,
        period,
        customPeriodStart,
        customPeriodEnd,
        groupMethod,
        loading,
        product,
        aggregatedPeriod,
        aggregatedComparison,
        cumulativeEntries,
        cumulativeGraphs
    } = props;
    const [graphTypeMap, setGraphTypeMap] = useState<GraphTypeMap>({});
    const [singleDay, singleYear] = useMemo(() => {
        let singleDay = true;
        let singleYear = true;
        if (entries.length > 1) {
            const firstDate = new IDate(entries[0].group_date);
            const lastDate = new IDate(entries[entries.length - 1].group_date);
            singleDay = firstDate.isAt(lastDate, 'day');
            singleYear = firstDate.isAt(lastDate, 'year');
        }
        return [singleDay, singleYear];
    }, [entries]);
    const graphData: SalesDashboardGraphData = useMemo(() => {
        const labels: Set<string> = new Set();
        const revenueValues: Array<number> = [];
        const cumulativeRevenueValues: Array<number> = [];
        const organicRevenueValues: Array<number> = [];
        const cumulativeOrganicRevenueValues: Array<number> = [];
        const sponsoredRevenueValues: Array<number> = [];
        const cumulativeSponsoredRevenueValues: Array<number> = [];
        const quantityValues: Array<number> = [];
        const cumulativeQuantityValues: Array<number> = [];
        const costsValues: Array<number> = [];
        const cumulativeCostsValues: Array<number> = [];
        const profitValues: Array<number> = [];
        const cumulativeProfitValues: Array<number> = [];
        const returnedQuantityValues: Array<number> = [];
        const cumulativeReturnedQuantityValues: Array<number> = [];
        const returnedRevenueValues: Array<number> = [];
        const cumulativeReturnedRevenueValues: Array<number> = [];
        for (const entry of entries) {
            const label: string = getGraphLabel(new IDate(entry.group_date));
            labels.add(label);
            revenueValues.push(entry.revenue_excl);
            organicRevenueValues.push(entry.organic_sales);
            sponsoredRevenueValues.push(entry.sponsored_sales);
            quantityValues.push(entry.quantity);
            costsValues.push(entry.costs);
            profitValues.push(entry.profit ?? 0);
            returnedQuantityValues.push(entry.return_quantity);
            returnedRevenueValues.push(entry.returned_revenue_excl);
        }
        for (const entry of cumulativeEntries) {
            const label: string = getGraphLabel(new IDate(entry.group_date));
            labels.add(label);
            cumulativeRevenueValues.push(roundToNumber(entry.revenue_excl, 2));
            cumulativeOrganicRevenueValues.push(roundToNumber(entry.organic_sales, 2));
            cumulativeSponsoredRevenueValues.push(roundToNumber(entry.sponsored_sales, 2));
            cumulativeQuantityValues.push(entry.quantity);
            cumulativeCostsValues.push(roundToNumber(entry.costs, 2));
            cumulativeProfitValues.push(roundToNumber(entry.profit ?? 0, 2));
            cumulativeReturnedQuantityValues.push(entry.return_quantity);
            cumulativeReturnedRevenueValues.push(roundToNumber(entry.returned_revenue_excl, 2));
        }
        return {
            labels: Array.from(labels),
            revenueValues,
            cumulativeRevenueValues,
            organicRevenueValues,
            cumulativeOrganicRevenueValues,
            sponsoredRevenueValues,
            cumulativeSponsoredRevenueValues,
            quantityValues,
            cumulativeQuantityValues,
            costsValues,
            cumulativeCostsValues,
            profitValues,
            cumulativeProfitValues,
            returnedQuantityValues,
            cumulativeReturnedQuantityValues,
            returnedRevenueValues,
            cumulativeReturnedRevenueValues
        };
    }, [entries, cumulativeEntries]);

    function onToggleGraphType(key: string) {
        return function () {
            setGraphTypeMap((currentMap: GraphTypeMap) => {
                const current = currentMap[key];
                const next = [undefined, 'area'].includes(current) ? 'bar' : 'area';
                return {...currentMap, [key]: next};
            });
        };
    }

    function getGraphLabel(groupDate: IDate): string {
        if (groupMethod === DataSplitMethod.HOUR) {
            if (singleDay) return groupDate.format('HH:mm');
            else if (singleYear) return groupDate.format('DD MMM HH:mm');
            return groupDate.format("DD MMM 'YY HH:mm");
        } else if (groupMethod === DataSplitMethod.DAY) {
            if (singleYear) return groupDate.format('DD MMM');
            return groupDate.format("DD MMM 'YY");
        } else if (groupMethod === DataSplitMethod.WEEK) {
            const weekNum = groupDate.week();
            if (singleYear) return `W${weekNum}`;
            const year = groupDate.year().toString().slice(-2);
            return `W${weekNum} '${year}`;
        } else if (groupMethod === DataSplitMethod.MONTH) {
            if (singleYear) return groupDate.format('MMMM');
            return groupDate.format("MMM 'YY");
        } else if (groupMethod === DataSplitMethod.QUARTER) {
            const quarterNum = Math.ceil(groupDate.quarter());
            if (singleYear) return `Q${quarterNum}`;
            const year = groupDate.year().toString().slice(-2);
            return `Q${quarterNum} '${year}`;
        } else return groupDate.format('YYYY');
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <div className='period-container'>
                    <BolmatePeriodSelector
                        onChange={onChangePeriod}
                        value={[customPeriodStart, customPeriodEnd]}
                        selectedPreset={period}
                        selectShape='rounded'
                        selectSize='small'
                    />
                </div>
                {renderStatCards()}
                {renderGraphs()}
            </div>
        );
    }

    function renderStatCards() {
        return (
            <div className='stat-blocks-container'>
                <BolmateStatCard
                    data={{
                        label: t('salesDashboardV2.productDetails.salesProductStats.quantitySold', 'Aantal verkocht'),
                        value: aggregatedPeriod.periodQuantity,
                        prevValue: aggregatedComparison.periodQuantity,
                        truncateThreshold: 100_000
                    }}
                />
                <BolmateStatCard
                    data={[
                        {
                            label: t(
                                'salesDashboardV2.productDetails.salesProductStats.revenueExcl',
                                'Omzet (excl. BTW)'
                            ),
                            value: aggregatedPeriod.periodRevenueExcl,
                            prevValue: aggregatedComparison.periodRevenueExcl,
                            truncateThreshold: 100_000,
                            valueType: StatCardDataType.CURRENCY
                        },
                        {
                            label: t(
                                'salesDashboardV2.productDetails.salesProductStats.revenueIncl',
                                'Omzet (incl. BTW)'
                            ),
                            value: aggregatedPeriod.periodRevenueIncl,
                            prevValue: aggregatedComparison.periodRevenueIncl,
                            truncateThreshold: 100_000,
                            valueType: StatCardDataType.CURRENCY
                        }
                    ]}
                />
                <BolmateStatCard
                    data={{
                        label: t('salesDashboardV2.productDetails.salesProductStats.costs', 'Kosten'),
                        value: aggregatedPeriod.periodCosts,
                        prevValue: aggregatedComparison.periodCosts,
                        valueType: StatCardDataType.CURRENCY,
                        truncateThreshold: 100_000
                    }}
                />
                <BolmateStatCard
                    data={{
                        label: t('salesDashboardV2.productDetails.salesProductStats.profitExcl', 'Winst (excl. BTW)'),
                        value: aggregatedPeriod.periodProfit,
                        prevValue: aggregatedComparison.periodProfit,
                        truncateThreshold: 100_000,
                        valueType: StatCardDataType.CURRENCY,
                        zeroAsDash: true,
                        alternates: [
                            {
                                label: t(
                                    'salesDashboardV2.productDetails.salesProductStats.avgMargin',
                                    'Marge (gemiddeld)'
                                ),
                                value: aggregatedPeriod.periodMargin,
                                prevValue: aggregatedComparison.periodMargin,
                                valueType: StatCardDataType.PERCENTAGE,
                                zeroAsDash: true
                            }
                        ]
                    }}
                />
                <BolmateStatCard
                    data={[
                        {
                            label: t(
                                'salesDashboardV2.productDetails.salesProductStats.returnQuantity',
                                'Aantal retour'
                            ),
                            value: aggregatedPeriod.periodReturnedQuantity,
                            prevValue: aggregatedComparison.periodReturnedQuantity,
                            truncateThreshold: 100_000,
                            invertTrend: true
                        },
                        {
                            label: t('salesDashboardV2.productDetails.salesProductStats.returnRevenue', 'Retour omzet'),
                            value: aggregatedPeriod.periodReturnedRevenue,
                            prevValue: aggregatedComparison.periodReturnedRevenue,
                            truncateThreshold: 100_000,
                            valueType: StatCardDataType.CURRENCY,
                            invertTrend: true
                        }
                    ]}
                />
                <BolmateStatCard
                    data={[
                        {
                            label: t(
                                'salesDashboardV2.productDetails.salesProductStats.organicRevenue',
                                'Omzet organisch'
                            ),
                            value: aggregatedPeriod.periodRevenueOrganic,
                            prevValue: aggregatedComparison.periodRevenueOrganic,
                            valueType: StatCardDataType.CURRENCY,
                            truncateThreshold: 100_000
                        },
                        {
                            label: t('salesDashboardV2.productDetails.salesProductStats.adRevenue', 'Omzet uit Ads'),
                            value: aggregatedPeriod.periodRevenueSponsored,
                            prevValue: aggregatedComparison.periodRevenueSponsored,
                            valueType: StatCardDataType.CURRENCY,
                            truncateThreshold: 100_000
                        }
                    ]}
                />
            </div>
        );
    }

    function toPlotSeries(name: string, labels: Array<string>, values: Array<number>): BolmatePlotSeries {
        return {
            id: name,
            name,
            data: labels.map((label, i) => ({date: label, value: values[i] ?? null}))
        };
    }

    function renderGraphs() {
        if (loading || !entries.length) return null;
        return (
            <>
                {renderGraph(
                    t('salesDashboardV2.productDetails.salesProductStats.quantitySold', 'Aantal verkocht'),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.quantitySold', 'Aantal verkocht'),
                        t(
                            'salesDashboardV2.productDetails.salesProductStats.quantitySoldCum',
                            'Aantal verkocht (opgeteld)'
                        )
                    ],
                    'quantityValues',
                    'cumulativeQuantityValues',
                    'quantity',
                    'generic'
                )}
                {renderGraph(
                    t('salesDashboardV2.productDetails.salesProductStats.revenueExcl', 'Omzet (excl. BTW)'),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.revenue', 'Omzet'),
                        t('salesDashboardV2.productDetails.salesProductStats.revenueCum', 'Omzet (opgeteld)')
                    ],
                    'revenueValues',
                    'cumulativeRevenueValues',
                    'revenue'
                )}
                {renderGraph(
                    t('salesDashboardV2.productDetails.salesProductStats.costs', 'Kosten'),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.costs', 'Kosten'),
                        t('salesDashboardV2.productDetails.salesProductStats.costsCum', 'Kosten (opgeteld)')
                    ],
                    'costsValues',
                    'cumulativeCostsValues',
                    'costs'
                )}
                {renderGraph(
                    t('salesDashboardV2.productDetails.salesProductStats.profitExcl', 'Winst (excl. BTW)'),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.profit', 'Winst'),
                        t('salesDashboardV2.productDetails.salesProductStats.profitCum', 'Winst (opgeteld)')
                    ],
                    'profitValues',
                    'cumulativeProfitValues',
                    'profit'
                )}
                {renderGraph(
                    t('salesDashboardV2.productDetails.salesProductStats.organicRevenue', 'Omzet organisch'),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.revenue', 'Omzet'),
                        t('salesDashboardV2.productDetails.salesProductStats.revenueCum', 'Omzet (opgeteld)')
                    ],
                    'organicRevenueValues',
                    'cumulativeOrganicRevenueValues',
                    'organicRevenue'
                )}
                {renderGraph(
                    t('salesDashboardV2.productDetails.salesProductStats.adRevenue', 'Omzet uit Ads'),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.revenue', 'Omzet'),
                        t('salesDashboardV2.productDetails.salesProductStats.revenueCum', 'Omzet (opgeteld)')
                    ],
                    'sponsoredRevenueValues',
                    'cumulativeSponsoredRevenueValues',
                    'sponsoredRevenue'
                )}
                {renderGraph(
                    t('salesDashboardV2.productDetails.salesProductStats.returnQuantity', 'Aantal retour'),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.returnQuantity', 'Aantal retour'),
                        t(
                            'salesDashboardV2.productDetails.salesProductStats.returnQuantityCum',
                            'Aantal retour (opgeteld)'
                        )
                    ],
                    'returnedQuantityValues',
                    'cumulativeReturnedQuantityValues',
                    'returnQuantity',
                    'generic'
                )}
                {renderGraph(
                    t(
                        'salesDashboardV2.productDetails.salesProductStats.returnRevenueExcl',
                        'Retour omzet (excl. BTW)'
                    ),
                    [
                        t('salesDashboardV2.productDetails.salesProductStats.returnRevenue', 'Retour omzet'),
                        t(
                            'salesDashboardV2.productDetails.salesProductStats.returnRevenueCum',
                            'Retour omzet (opgeteld)'
                        )
                    ],
                    'returnedRevenueValues',
                    'cumulativeReturnedRevenueValues',
                    'returnRevenue'
                )}
            </>
        );
    }

    function renderGraph(
        title: string,
        serieNames: [string, string],
        dataKey: keyof Omit<SalesDashboardGraphData, 'labels'>,
        cumulativeKey: keyof Omit<SalesDashboardGraphData, 'labels'>,
        graphKey: string,
        valueType: 'currency' | 'generic' = 'currency'
    ) {
        const chartData = [toPlotSeries(serieNames[0], graphData.labels, graphData[dataKey])];
        const cumulativeData = [toPlotSeries(serieNames[1], graphData.labels, graphData[cumulativeKey])];
        return renderGraphContainer(chartData, cumulativeData, title, graphKey, valueType);
    }

    function renderGraphContainer(
        splitData: Array<BolmatePlotSeries>,
        cumulativeData: Array<BolmatePlotSeries>,
        title: string,
        graphKey: string,
        valueType: 'currency' | 'generic'
    ) {
        const graphType = graphTypeMap[graphKey] ?? 'area';
        const graphToggleIcon = graphType === 'area' ? 'mdi:bar-chart' : 'mdi:chart-line-variant';
        const graphToggleTooltip =
            graphType === 'area'
                ? t('salesDashboardV2.productDetails.salesProductStats.barGraph', 'Staafdiagram')
                : t('salesDashboardV2.productDetails.salesProductStats.lineGraph', 'Lijndiagram');
        const cumulativeDisplay = cumulativeGraphs.has(graphKey);
        const useData = cumulativeDisplay ? cumulativeData : splitData;

        const minTicks = Math.min(...useData.map(s => s.data.length));
        const options: ApexOptions = {
            xaxis: {tickAmount: Math.min(15, minTicks)}
        };
        if (valueType === 'currency') {
            options['tooltip'] = {y: {formatter: (value: number) => formatCurrency(value)}};
            options['yaxis'] = {labels: {formatter: (value: number) => formatCurrency(value, '-', 'compact')}};
        }
        return (
            <div className='graph-container'>
                <div className='graph-header'>
                    <BolmateTooltip text={graphToggleTooltip}>
                        <div className='icon-btn' onClick={onToggleGraphType(graphKey)}>
                            <Icon icon={graphToggleIcon} />
                        </div>
                    </BolmateTooltip>
                    <label className='graph-title'>{title}</label>
                    <BolmateSwitch
                        value={cumulativeDisplay}
                        onChange={onChangeCumulativeGraphs(graphKey)}
                        labels={[t('salesDashboardV2.productDetails.salesProductStats.cumulative', 'Opgeteld')]}
                    />
                </div>
                <BolmatePlot series={useData} type={graphType} options={options} legend={false} />
            </div>
        );
    }

    function renderFooter() {
        if (!product) return null;
        return (
            <div className='footer-container'>
                <BolmateButton
                    onClick={onNavigateBol}
                    label={t('salesDashboardV2.productDetails.salesProductStats.viewOnBol', 'Bekijken op Bol')}
                    disabled={!product.global_id}
                    ornaments={{element: <AwesomeIcon name='arrow-up-right-from-square' />}}
                />
                <BolmateButton
                    onClick={onChangeView(SalesProductView.EDIT_PRODUCT)}
                    label={t('salesDashboardV2.productDetails.salesProductStats.editProduct', 'Productinstellingen')}
                    ornaments={{element: <AwesomeIcon name='edit' />}}
                    secondary
                />
            </div>
        );
    }

    return (
        <div className='sales-product-stats'>
            <Loader active={loading} />
            {renderBody()}
            {renderFooter()}
        </div>
    );
}
